<template>
  <div v-if="isVisible" class="custom-modal-wrapper">
    <div class="custom-modal-backdrop" @click="hide"></div>
    <div class="custom-modal">
      <div class="custom-modal-header">
        <h5 class="custom-modal-title">Frage bearbeiten</h5>
        <button type="button" class="custom-modal-close" @click="hide">×</button>
      </div>
      <div class="custom-modal-body">
        <div class="question-form">
          <form @submit.prevent="updateQuestion">
            <div class="form-group mb-3">
              <label for="questionType" class="form-label fw-bold">Fragetyp</label>
              <select 
                id="questionType" 
                v-model="editableQuestion.type" 
                class="form-select"
                required
                :disabled="true"
              >
                <option value="multiple_choice">Multiple-Choice (Auswahl)</option>
                <option value="open">Offene Frage</option>
              </select>
            </div>
            
            <!-- Auswahlmöglichkeit für Single- oder Multiple-Choice -->
            <div class="form-group mb-3" v-if="editableQuestion.type === 'multiple_choice'">
              <label class="form-label fw-bold">Antwortmöglichkeiten</label>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  id="singleAnswer" 
                  name="answerType" 
                  :value="false" 
                  v-model="editableQuestion.allow_multiple_answers"
                >
                <label class="form-check-label" for="singleAnswer">
                  Eine Antwort möglich (Single-Choice)
                </label>
              </div>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="radio" 
                  id="multipleAnswers" 
                  name="answerType" 
                  :value="true" 
                  v-model="editableQuestion.allow_multiple_answers"
                >
                <label class="form-check-label" for="multipleAnswers">
                  Mehrere Antworten möglich (Multiple-Choice)
                </label>
              </div>
            </div>
            
            <div class="mb-3">
              <label for="questionDescription" class="form-label">Fragebeschreibung</label>
              <textarea 
                class="form-control" 
                id="questionDescription" 
                v-model="editableQuestion.description" 
                rows="3"
                placeholder="Geben Sie Ihre Frage ein..."
                required
              ></textarea>
            </div>

            <!-- Bildupload für Fragen -->
            <div class="form-group mb-3">
              <label for="questionImage" class="form-label fw-bold">Bild (optional)</label>
              <div class="custom-file mb-2">
                <input 
                  type="file" 
                  class="form-control" 
                  id="questionImage" 
                  @change="handleQuestionImageUpload"
                  accept="image/*"
                />
              </div>
              
              <!-- Bildvorschau, falls vorhanden -->
              <div v-if="editableQuestion.image_url || questionImagePreview" class="mt-2">
                <div class="question-image-preview">
                  <img 
                    :src="questionImagePreview || editableQuestion.image_url" 
                    alt="Bildvorschau" 
                    class="img-thumbnail"
                    style="max-height: 200px;"
                  />
                </div>
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-danger mt-2" 
                  @click="removeQuestionImage"
                >
                  <i class="fas fa-trash-alt me-1"></i> Bild entfernen
                </button>
              </div>
            </div>
            
            <!-- Hinweis zu Antworten -->
            <div class="alert alert-info mb-3">
              <i class="fas fa-info-circle me-2"></i>
              <strong>Hinweis:</strong> Mindestens 2 Antworten sind erforderlich. Die weiteren Antworten sind optional. 
              Mindestens eine Antwort muss als richtig markiert werden.
            </div>
            
            <!-- Antworten für Multiple-Choice-Fragen -->
            <div v-if="editableQuestion.type === 'multiple_choice'" class="answers-container">
              <h5 class="mb-3">Antwortmöglichkeiten</h5>
              
              <div v-for="i in 6" :key="`answer-${i}`" class="answer-item mb-3">
                <div class="input-group">
                  <!-- Single-Choice: Radio-Buttons -->
                  <div v-if="!editableQuestion.allow_multiple_answers" class="input-group-text">
                    <input 
                      type="radio" 
                      :id="`is_answer_${i}_correct`" 
                      v-model="singleCorrectAnswer" 
                      :value="i" 
                      class="form-check-input"
                      @change="setSingleCorrectAnswer(i)"
                    >
                  </div>
                  
                  <!-- Multiple-Choice: Checkboxen -->
                  <div v-else class="input-group-text">
                    <input 
                      type="checkbox" 
                      :id="`is_answer_${i}_correct`" 
                      v-model="editableQuestion[`is_answer_${i}_correct`]" 
                      class="form-check-input"
                    >
                  </div>
                  
                  <input 
                    type="text" 
                    class="form-control" 
                    :placeholder="`Antwort ${i}`" 
                    v-model="editableQuestion[`answer_${i}`]"
                  >
                </div>
              </div>
            </div>
            
            <div class="form-group mb-3">
              <label for="questionPoints" class="form-label fw-bold">Punkte</label>
              <input 
                type="number" 
                id="questionPoints" 
                v-model.number="editableQuestion.points" 
                class="form-control"
                min="0"
                :placeholder="defaultPointsPerQuestion + ' (Standard)'"
              />
              <small class="form-text text-muted">
                Leer lassen für Standardpunkte ({{ defaultPointsPerQuestion }} Punkte)
              </small>
            </div>
            
            <div class="d-flex justify-content-between mt-4">
              <button 
                type="button" 
                class="btn btn-outline-secondary" 
                @click="hide"
              >
                Abbrechen
              </button>
              <button 
                type="submit" 
                class="btn btn-primary"
                :disabled="loading"
              >
                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-else class="fas fa-check me-2"></i>
                Speichern
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EditQuestionModal',
  emits: [
    'save',
    'reset',
    'image-updated',
    'image-removed'
  ],
  props: {
    question: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    defaultPointsPerQuestion: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      isVisible: false,
      editableQuestion: {},
      questionImagePreview: null,
      questionImageFile: null,
      originalAnswers: [],
      singleCorrectAnswer: null
    };
  },
  methods: {
    show(question) {
      console.log('EditQuestionModal: Zeige Modal an mit Frage:', question);
      document.body.style.overflow = 'hidden'; // Body-Scrolling deaktivieren
      
      if (question) {
        console.log('EditQuestionModal: Frage aus Parameter übernommen');
        // Tiefe Kopie der Frage erstellen
        this.editableQuestion = JSON.parse(JSON.stringify(question));
        
        // Wenn answer_X Felder vorhanden sind, kopiere sie in die editableQuestion
        for (let i = 1; i <= 6; i++) {
          if (question[`answer_${i}`]) {
            this.editableQuestion[`answer_${i}`] = question[`answer_${i}`];
          }
          if (question[`is_answer_${i}_correct`] !== undefined) {
            this.editableQuestion[`is_answer_${i}_correct`] = question[`is_answer_${i}_correct`];
          }
        }
        
        // Bild-URL setzen, falls vorhanden
        this.questionImagePreview = question.image_url;
        
        // Initialisiere den singleCorrectAnswer für Radio-Buttons
        this.initializeSingleCorrectAnswer();
      } else if (Object.keys(this.editableQuestion).length === 0) {
        console.log('EditQuestionModal: Keine Frage übergeben, verwende leere Frage');
        // Leere Frage erstellen
        this.editableQuestion = {
          id: null,
          type: 'multiple_choice',
          description: '',
          points: this.defaultPointsPerQuestion,
          image_url: null,
          allow_multiple_answers: false,
          answer_1: '',
          answer_2: '',
          answer_3: '',
          answer_4: '',
          answer_5: '',
          answer_6: '',
          is_answer_1_correct: false,
          is_answer_2_correct: false,
          is_answer_3_correct: false,
          is_answer_4_correct: false,
          is_answer_5_correct: false,
          is_answer_6_correct: false
        };
      }
      
      console.log('EditQuestionModal: Finale editableQuestion:', this.editableQuestion);
      this.isVisible = true;
    },
    hide() {
      console.log('EditQuestionModal: Verstecke Modal');
      document.body.style.overflow = ''; // Body-Scrolling wiederherstellen
      this.isVisible = false;
      this.$emit('reset');
    },
    handleQuestionImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!file.type.match('image.*')) {
        this.$toast.error('Bitte laden Sie ein gültiges Bild hoch.');
        return;
      }
      
      this.questionImageFile = file;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        this.questionImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      this.$emit('image-updated', file);
    },
    removeQuestionImage() {
      this.questionImageFile = null;
      this.questionImagePreview = null;
      this.editableQuestion.image_url = null;
      
      this.$emit('image-removed');
    },
    setSingleCorrectAnswer(index) {
      // Alle Antworten auf falsch setzen
      for (let i = 1; i <= 6; i++) {
        this.editableQuestion[`is_answer_${i}_correct`] = false;
      }
      // Nur die ausgewählte Antwort auf wahr setzen
      this.editableQuestion[`is_answer_${index}_correct`] = true;
    },
    initializeSingleCorrectAnswer() {
      if (!this.editableQuestion.allow_multiple_answers) {
        // Finde die erste korrekte Antwort
        for (let i = 1; i <= 6; i++) {
          if (this.editableQuestion[`is_answer_${i}_correct`]) {
            this.singleCorrectAnswer = i;
            return;
          }
        }
        // Wenn keine korrekte Antwort gefunden wurde, setze auf 0
        this.singleCorrectAnswer = 0;
      }
    },
    updateQuestion() {
      console.log('EditQuestionModal: updateQuestion aufgerufen');
      // Validierung und Aufbereitung der Daten
      const questionToSave = this.prepareQuestionForSave();
      
      if (questionToSave) {
        console.log('EditQuestionModal: Frage zum Speichern:', questionToSave);
        // Wichtig: Sende die vollständig bearbeitete Frage zurück
        this.$emit('save', questionToSave, true);
        // Das Modal wird vom Empfänger geschlossen
      } else {
        console.error('EditQuestionModal: Validierung fehlgeschlagen');
      }
    },
    prepareQuestionForSave() {
      // Kopie der Frage erstellen
      const question = { ...this.editableQuestion };
      console.log('EditQuestionModal: Bereite Frage vor. Original:', this.editableQuestion);
      
      // Sicherstellen, dass die Beschreibung ausgefüllt ist
      if (!question.description || question.description.trim() === '') {
        this.$toast.error('Bitte geben Sie eine Fragebeschreibung ein');
        return null;
      }
      
      // Sicherstellen, dass der Typ korrekt ist
      if (question.type !== 'multiple_choice' && question.type !== 'open') {
        question.type = 'multiple_choice';
      }
      
      // Punkte validieren
      if (!question.points || question.points < 1) {
        question.points = this.defaultPointsPerQuestion; // Standardwert
      }
      
      // Bei Multiple-Choice-Fragen sicherstellen, dass mindestens eine Antwort korrekt ist
      if (question.type === 'multiple_choice') {
        const hasCorrectAnswer = 
          question.is_answer_1_correct || 
          question.is_answer_2_correct || 
          question.is_answer_3_correct || 
          question.is_answer_4_correct || 
          question.is_answer_5_correct || 
          question.is_answer_6_correct;
        
        if (!hasCorrectAnswer) {
          this.$toast.error('Bitte markieren Sie mindestens eine korrekte Antwort');
          return null;
        }
        
        // Bei Single-Choice (allow_multiple_answers = false) sicherstellen, dass nur eine Antwort korrekt ist
        if (!question.allow_multiple_answers) {
          const correctAnswersCount = 
            (question.is_answer_1_correct ? 1 : 0) + 
            (question.is_answer_2_correct ? 1 : 0) + 
            (question.is_answer_3_correct ? 1 : 0) + 
            (question.is_answer_4_correct ? 1 : 0) + 
            (question.is_answer_5_correct ? 1 : 0) + 
            (question.is_answer_6_correct ? 1 : 0);
          
          if (correctAnswersCount > 1) {
            this.$toast.error('Bei Einfachauswahl (Single-Choice) darf nur eine Antwort korrekt sein');
            return null;
          }
        }
      }
      
      console.log('EditQuestionModal: Frage erfolgreich vorbereitet:', question);
      return question;
    }
  }
};
</script>

<style lang="scss" scoped>
.custom-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.custom-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
}

.custom-modal {
  position: relative;
  background-color: #fff;
  width: 90%;
  max-width: 800px;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  max-height: 90vh;
  overflow-y: auto;
}

.custom-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #4776E6, #8E54E9);
  color: white;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.custom-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.custom-modal-close {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  margin: 0;
}

.custom-modal-body {
  padding: 1.5rem;
}

.question-form {
  .form-group {
    margin-bottom: 1.25rem;
  }
  
  .question-image-preview {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.08));
    display: inline-block;
    margin-bottom: 0.75rem;
    
    img {
      max-width: 100%;
      border-radius: 8px;
    }
  }
}

.answer-item {
  position: relative;
  
  .input-group {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    
    .input-group-text {
      background-color: #f8f9fa;
      border-right: none;
    }
    
    .form-control {
      border-left: none;
      border-radius: 0 8px 8px 0;
    }
    
    .btn {
      border-radius: 0 8px 8px 0;
    }
  }
}

.optional-label {
  position: absolute;
  top: -5px;
  right: 10px;
  background: #f8f9fa;
  padding: 0 5px;
  border-radius: 3px;
  font-size: 0.75rem;
  z-index: 1;
}
</style> 